// services/ticket.service.ts

export type TicketStatus = "open" | "in_progress" | "resolved" | "closed";
export type TicketPriority = "critical" | "high" | "medium" | "low";
export type TicketCategory =
  | "Internship"
  | "Placement"
  | "Training"
  | "Academic"
  | "Technical"
  | "General";

export interface TicketRaisedBy {
  name: string;
  role: string; // "Student" | "Staff"
  rollNo?: string;
  email?: string;
  phone?: string;
}

export interface TicketAssignedTo {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
}

export interface TicketComment {
  id: string;
  author: string;
  role: string;
  timestamp: string;
  content: string;
}

export interface TicketTimeline {
  id: string;
  type: "created" | "status_updated" | "comment_added" | "priority_changed";
  title: string;
  description?: string;
  author: string;
  timestamp: string;
}

export interface RelatedModule {
  type: string;
  details: string;
  link: string;
}

export interface TicketType {
  _id: string;
  ticketNumber: string; // e.g. "TKT-2025-001"
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  raisedBy: TicketRaisedBy;
  assignedTo: TicketAssignedTo;
  comments: TicketComment[];
  timeline: TicketTimeline[];
  relatedModule?: RelatedModule;
  createdAt: string; // ISO date string
  updatedAt: string;
}

export interface TicketCreateType {
  title: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  raisedBy: TicketRaisedBy;
  assignedTo: TicketAssignedTo;
}

export interface TicketMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type TicketParamsType = {
  search?: string;
  status?: TicketStatus | "all";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_TICKETS: TicketType[] = [
  {
    _id: "1",
    ticketNumber: "TKT-2025-001",
    title: "Internship certificate not received",
    description:
      "I completed my internship at Google but haven't received the certificate yet. It has been 3 weeks since completion.",
    status: "open",
    priority: "medium",
    category: "Internship",
    raisedBy: {
      name: "Alex Johnson",
      role: "Student",
      rollNo: "IT2021001",
      email: "alex@student.college.edu",
      phone: "+1-234-567-9001",
    },
    assignedTo: {
      name: "Prof. John Davis",
      role: "Placement Coordinator",
      email: "john.davis@college.edu",
      phone: "+1-234-567-8001",
    },
    comments: [],
    timeline: [
      {
        id: "t1",
        type: "created",
        title: "Ticket Created",
        author: "Alex Johnson",
        timestamp: "2025-01-13T09:00:00.000Z",
      },
    ],
    createdAt: "2025-01-13T00:00:00.000Z",
    updatedAt: "2025-01-13T00:00:00.000Z",
  },
  {
    _id: "2",
    ticketNumber: "TKT-2025-002",
    title: "Placement offer letter issue",
    description:
      "The offer letter from Microsoft has incorrect joining date. Need to get it corrected before I submit to HR.",
    status: "in_progress",
    priority: "high",
    category: "Placement",
    raisedBy: {
      name: "Emma Watson",
      role: "Student",
      rollNo: "IT2021045",
      email: "emma.w@student.college.edu",
      phone: "+1-234-567-9002",
    },
    assignedTo: {
      name: "Prof. John Davis",
      role: "Placement Coordinator",
      email: "john.davis@college.edu",
      phone: "+1-234-567-8001",
    },
    comments: [
      {
        id: "c1",
        author: "Emma Watson",
        role: "Student",
        timestamp: "2025-01-12T09:15:00.000Z",
        content:
          "The joining date shows June 1, 2025 but it should be July 1, 2025 as per the verbal communication during HR round.",
      },
      {
        id: "c2",
        author: "Prof. John Davis",
        role: "Placement Coordinator",
        timestamp: "2025-01-14T11:20:00.000Z",
        content:
          "I have contacted Microsoft HR team. They confirmed the date should be July 1st. They will send the corrected offer letter by tomorrow.",
      },
    ],
    timeline: [
      {
        id: "t1",
        type: "created",
        title: "Ticket Created",
        description: "Issue raised about incorrect offer letter details",
        author: "Emma Watson",
        timestamp: "2025-01-12T09:15:00.000Z",
      },
      {
        id: "t2",
        type: "status_updated",
        title: "Status Updated",
        description: "Status changed from Open to In Progress",
        author: "Prof. John Davis",
        timestamp: "2025-01-12T14:30:00.000Z",
      },
      {
        id: "t3",
        type: "comment_added",
        title: "Comment Added",
        description: "Contacted Microsoft HR for correction",
        author: "Prof. John Davis",
        timestamp: "2025-01-14T11:20:00.000Z",
      },
    ],
    relatedModule: {
      type: "Placement",
      details: "Software Engineer at Microsoft",
      link: "/activities/placement/1",
    },
    createdAt: "2025-01-12T00:00:00.000Z",
    updatedAt: "2025-01-14T11:20:00.000Z",
  },
  {
    _id: "3",
    ticketNumber: "TKT-2025-003",
    title: "Training portal access issue",
    description:
      "Unable to access the CodeMaster training portal. Login credentials are not working.",
    status: "resolved",
    priority: "medium",
    category: "Training",
    raisedBy: {
      name: "Sophia Brown",
      role: "Student",
      rollNo: "IT2021088",
      email: "sophia.b@student.college.edu",
      phone: "+1-234-567-9003",
    },
    assignedTo: {
      name: "IT Support Team",
      role: "Support Desk",
      email: "support@college.edu",
      phone: "+1-234-567-0000",
    },
    comments: [],
    timeline: [
      {
        id: "t1",
        type: "created",
        title: "Ticket Created",
        author: "Sophia Brown",
        timestamp: "2025-01-10T09:00:00.000Z",
      },
      {
        id: "t2",
        type: "status_updated",
        title: "Status Fixed",
        description: "Credentials reset and verified",
        author: "IT Support Team",
        timestamp: "2025-01-10T15:00:00.000Z",
      },
    ],
    createdAt: "2025-01-10T00:00:00.000Z",
    updatedAt: "2025-01-10T00:00:00.000Z",
  },
  {
    _id: "4",
    ticketNumber: "TKT-2025-004",
    title: "CGPA calculation error",
    description:
      "My CGPA is showing 8.5 in the system but it should be 8.7 as per my grade sheet. Please verify.",
    status: "in_progress",
    priority: "high",
    category: "Academic",
    raisedBy: {
      name: "Michael Chen",
      role: "Student",
      rollNo: "IT2021012",
      email: "michael.c@student.college.edu",
      phone: "+1-234-567-9004",
    },
    assignedTo: {
      name: "Dr. Lisa Anderson",
      role: "Academic Dean",
      email: "lisa.anderson@college.edu",
      phone: "+1-234-567-8002",
    },
    comments: [],
    timeline: [],
    createdAt: "2025-01-11T00:00:00.000Z",
    updatedAt: "2025-01-11T00:00:00.000Z",
  },
  {
    _id: "5",
    ticketNumber: "TKT-2025-005",
    title: "Student database sync issue",
    description:
      "Student records are not syncing properly between placement and training modules.",
    status: "open",
    priority: "critical",
    category: "Technical",
    raisedBy: {
      name: "Prof. Sarah Johnson",
      role: "Staff",
      email: "sarah.j@college.edu",
      phone: "+1-234-567-8003",
    },
    assignedTo: {
      name: "IT Admin",
      role: "System Administrator",
      email: "admin@college.edu",
      phone: "+1-234-567-0001",
    },
    comments: [],
    timeline: [],
    createdAt: "2025-01-14T00:00:00.000Z",
    updatedAt: "2025-01-14T00:00:00.000Z",
  },
  {
    _id: "6",
    ticketNumber: "TKT-2025-006",
    title: "Soft skills workshop schedule conflict",
    description:
      "The leadership workshop scheduled for Jan 20 conflicts with semester exams. Need to reschedule.",
    status: "open",
    priority: "low",
    category: "Training",
    raisedBy: {
      name: "Oliver Smith",
      role: "Student",
      rollNo: "IT2021056",
      email: "oliver.s@student.college.edu",
      phone: "+1-234-567-9006",
    },
    assignedTo: {
      name: "Dr. Michael Roberts",
      role: "Training Head",
      email: "michael.r@college.edu",
      phone: "+1-234-567-8004",
    },
    comments: [],
    timeline: [],
    createdAt: "2025-01-13T00:00:00.000Z",
    updatedAt: "2025-01-13T00:00:00.000Z",
  },
  {
    _id: "7",
    ticketNumber: "TKT-2025-007",
    title: "Placement drive registration not working",
    description:
      "Cannot register for the Amazon placement drive. Submit button is not responding.",
    status: "resolved",
    priority: "high",
    category: "Technical",
    raisedBy: {
      name: "Rachel Kim",
      role: "Student",
      rollNo: "IT2021023",
      email: "rachel.k@student.college.edu",
      phone: "+1-234-567-9007",
    },
    assignedTo: {
      name: "IT Support Team",
      role: "Support Desk",
      email: "support@college.edu",
      phone: "+1-234-567-0000",
    },
    comments: [],
    timeline: [],
    createdAt: "2025-01-09T00:00:00.000Z",
    updatedAt: "2025-01-09T00:00:00.000Z",
  },
  {
    _id: "8",
    ticketNumber: "TKT-2025-008",
    title: "Internship stipend not credited",
    description:
      "Completed internship at Intel last month but stipend amount hasn't been credited to my account.",
    status: "in_progress",
    priority: "high",
    category: "Internship",
    raisedBy: {
      name: "Liam Wilson",
      role: "Student",
      rollNo: "IT2021034",
      email: "liam.w@student.college.edu",
      phone: "+1-234-567-9008",
    },
    assignedTo: {
      name: "Finance Department",
      role: "Accountant",
      email: "finance@college.edu",
      phone: "+1-234-567-0002",
    },
    comments: [],
    timeline: [],
    createdAt: "2025-01-12T00:00:00.000Z",
    updatedAt: "2025-01-12T00:00:00.000Z",
  },
  {
    _id: "9",
    ticketNumber: "TKT-2025-009",
    title: "Vendor contact information update",
    description:
      "Need to update contact details for SkillUp Training vendor. Primary contact has changed.",
    status: "closed",
    priority: "low",
    category: "General",
    raisedBy: {
      name: "Prof. Mark Thompson",
      role: "Staff",
      email: "mark.t@college.edu",
      phone: "+1-234-567-8005",
    },
    assignedTo: {
      name: "Admin Office",
      role: "Administrator",
      email: "admin.office@college.edu",
      phone: "+1-234-567-0003",
    },
    comments: [],
    timeline: [],
    createdAt: "2025-01-08T00:00:00.000Z",
    updatedAt: "2025-01-08T00:00:00.000Z",
  },
  {
    _id: "10",
    ticketNumber: "TKT-2025-010",
    title: "Personality assessment scores not visible",
    description:
      "Students cannot view their personality assessment scores in the portal. Need to fix the display issue.",
    status: "open",
    priority: "medium",
    category: "Technical",
    raisedBy: {
      name: "Dr. Lisa Anderson",
      role: "Staff",
      email: "lisa.anderson@college.edu",
      phone: "+1-234-567-8002",
    },
    assignedTo: {
      name: "IT Admin",
      role: "System Administrator",
      email: "admin@college.edu",
      phone: "+1-234-567-0001",
    },
    comments: [],
    timeline: [],
    createdAt: "2025-01-14T00:00:00.000Z",
    updatedAt: "2025-01-14T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  tickets: TicketType[],
  filters: TicketParamsType
): { data: TicketType[]; total: number } => {
  let result = [...tickets];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.ticketNumber.toLowerCase().includes(q) ||
        t.raisedBy.name.toLowerCase().includes(q) ||
        t.assignedTo.name.toLowerCase().includes(q)
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((t) => t.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllTickets = async (filters: TicketParamsType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/tickets/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_TICKETS, filters);
  return {
    tickets: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as TicketMetaType,
  };
};

export const fetchTicketById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/tickets/${_id}`);
  const ticket = DUMMY_TICKETS.find((t) => t._id === _id);
  if (!ticket) throw new Error("Ticket not found");
  return ticket;
};

export const createTicket = async (data: TicketCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/tickets/", data);
  const newTicket: TicketType = {
    ...data,
    _id: String(Date.now()),
    ticketNumber: `TKT-2025-${String(DUMMY_TICKETS.length + 1).padStart(3, "0")}`,
    comments: [],
    timeline: [
      {
        id: String(Date.now() + 1),
        type: "created",
        title: "Ticket Created",
        author: data.raisedBy.name,
        timestamp: new Date().toISOString(),
      },
    ],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_TICKETS.push(newTicket);
  return newTicket;
};

export const updateTicket = async (_id: string, data: TicketCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/tickets/${_id}`, data);
  const index = DUMMY_TICKETS.findIndex((t) => t._id === _id);
  if (index === -1) throw new Error("Ticket not found");
  DUMMY_TICKETS[index] = {
    ...DUMMY_TICKETS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_TICKETS[index];
};

export const deleteTicket = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/tickets/${_id}`);
  const index = DUMMY_TICKETS.findIndex((t) => t._id === _id);
  if (index === -1) throw new Error("Ticket not found");
  const [deleted] = DUMMY_TICKETS.splice(index, 1);
  return deleted;
};
